import logger from '../utils/logger.js';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Computer as SoftwareIcon,
  MedicalServices as SuperDoctorIcon,
  Tune as ModeIcon,
  PlayArrow as PlayArrowIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import { PERSONA_COLORS } from '../theme';
import PatientProfileAvatar from '../components/PatientProfileAvatar';
import CustomPatientProfileAvatar from '../components/myPatients/CustomPatientProfileAvatar';
import SessionModeToggle from '../components/SessionModeToggle';
import axios from 'axios';
import { listMyPatients } from '../utils/userContentApi';
import {
  DEFAULT_SESSION_MODE,
  sessionModeLabel,
} from '../utils/sessionMode';
import { sessionModeOptionsForUser, userHasMyHospitalModule } from '../utils/moduleAccess';
import { patientGenderLabel } from '../utils/myPatients';

const CUSTOM_PATIENT_PAGE = 50;

function customPatientPickerRow(patient) {
  return {
    id: patient.id,
    source: 'custom',
    personal: patient.personal,
    name: patient.personal?.name || 'Unnamed patient',
    age: patient.personal?.age || '',
    gender: patientGenderLabel(patient) || patient.personal?.sex || '',
    chief_complaint: patient.pathology?.chiefComplaint || '',
  };
}

const setupSectionTitleSx = {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
};

const PersonaSelection = () => {
  const navigate = useNavigate();
  const { user, isGuest, isAuthenticated } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [customPatients, setCustomPatients] = useState([]);
  const [customTotal, setCustomTotal] = useState(0);
  const [customLoadingMore, setCustomLoadingMore] = useState(false);
  const customRequestRef = React.useRef(0);
  const [, setSoftwarePersonas] = useState([]);
  const [, setSuperDoctors] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [selectedSuperDoctor, setSelectedSuperDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [startingSimulation, setStartingSimulation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMode, setSelectedMode] = useState(DEFAULT_SESSION_MODE);
  const sessionModeOptions = React.useMemo(
    () => sessionModeOptionsForUser(user),
    [user]
  );
  const myHospitalEnabled = userHasMyHospitalModule(user);

  useEffect(() => {
    if (selectedMode === 'nurse' && !sessionModeOptions.some((option) => option.value === 'nurse')) {
      setSelectedMode(DEFAULT_SESSION_MODE);
    }
  }, [selectedMode, sessionModeOptions]);

  const fetchPersonas = useCallback(async () => {
    try {
      logger.info('🎭 Fetching personas...');
      logger.info('🔐 Auth state:', { user: !!user, isGuest, isAuthenticated });
      
      const headers = {};
      const token = localStorage.getItem('token');
      
      if (token && user && !isGuest) {
        headers.Authorization = `Bearer ${token}`;
        logger.info('🔑 Making authenticated request with token');
      } else if (isGuest) {
        logger.info('👤 Making request as guest user (no auth required)');
      } else {
        logger.info('⚠️ Making unauthenticated request - user not logged in');
      }

      // Use the combined endpoint for all personas
      const response = await axios.get('/api/simulation/available-personas', { headers });

      logger.info('✅ Fetched personas:', {
        patients: response.data.patients?.length,
        software: response.data.software?.length,
        superDoctors: response.data.superDoctors?.length
      });

      setPatients(response.data.patients || []);
      setSoftwarePersonas(response.data.software || []);
      setSuperDoctors(response.data.superDoctors || []);
      
      if (response.data.software?.length > 0) {
        setSelectedSoftware(response.data.software[0]);
      }
      if (response.data.superDoctors?.length > 0) {
        setSelectedSuperDoctor(response.data.superDoctors[0]);
      }
    } catch (error) {
      logger.error('❌ Failed to fetch personas:', error);
      setError('Failed to load Anatomage Patients. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, isGuest, isAuthenticated]);

  // Filter and sort patients based on search term and character ID
  const filteredAndSortedPatients = React.useMemo(() => {
    let filtered = patients;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = patients.filter(patient => 
        patient.name?.toLowerCase().includes(searchLower) ||
        patient.chief_complaint?.toLowerCase().includes(searchLower) ||
        patient.age?.toString().includes(searchLower) ||
        patient.gender?.toLowerCase().includes(searchLower) ||
        String(patient.character_id ?? patient.id ?? '').includes(searchLower)
      );
    }
    
    // Sort by character ID (assuming it's a numeric field)
    return filtered.sort((a, b) => {
      const aId = parseInt(a.character_id || a.id) || 0;
      const bId = parseInt(b.character_id || b.id) || 0;
      return aId - bId;
    });
  }, [patients, searchTerm]);

  const selectablePatients = React.useMemo(() => {
    // Nurse mode routes only through Intent 3, which needs a v2 patient.
    const library = selectedMode === 'nurse'
      ? filteredAndSortedPatients.filter((patient) => patient.has_v2_context)
      : filteredAndSortedPatients;
    if (selectedMode !== 'medical_diagnosis' || !customPatients.length || !myHospitalEnabled) {
      return library;
    }
    return [...customPatients, ...library];
  }, [filteredAndSortedPatients, customPatients, selectedMode, myHospitalEnabled]);

  useEffect(() => {
    if (selectedMode !== 'medical_diagnosis' || !user || isGuest || !myHospitalEnabled) {
      setCustomPatients([]);
      setCustomTotal(0);
      return undefined;
    }
    const requestId = ++customRequestRef.current;
    const q = searchTerm.trim();
    const handle = setTimeout(() => {
      listMyPatients({ limit: CUSTOM_PATIENT_PAGE, offset: 0, q })
        .then((page) => {
          if (requestId !== customRequestRef.current) return;
          setCustomPatients((page.patients || []).map(customPatientPickerRow));
          setCustomTotal(page.total || 0);
        })
        .catch((error) => {
          if (requestId !== customRequestRef.current) return;
          logger.error('Failed to load custom patients:', error);
          setCustomPatients([]);
          setCustomTotal(0);
        });
    }, q ? 250 : 0);
    return () => clearTimeout(handle);
  }, [selectedMode, user, isGuest, searchTerm, myHospitalEnabled]);

  const loadMoreCustomPatients = () => {
    if (customLoadingMore || customPatients.length >= customTotal) return;
    const requestId = customRequestRef.current;
    const q = searchTerm.trim();
    setCustomLoadingMore(true);
    listMyPatients({ limit: CUSTOM_PATIENT_PAGE, offset: customPatients.length, q })
      .then((page) => {
        if (requestId !== customRequestRef.current) return;
        setCustomPatients((prev) => [...prev, ...(page.patients || []).map(customPatientPickerRow)]);
        setCustomTotal(page.total || 0);
      })
      .catch((error) => {
        logger.error('Failed to load more custom patients:', error);
      })
      .finally(() => {
        if (requestId === customRequestRef.current) setCustomLoadingMore(false);
      });
  };

  useEffect(() => {
    const notAllowed = selectedMode !== 'medical_diagnosis' && selectedPatient?.source === 'custom';
    const notV2ForNurse = selectedMode === 'nurse' && selectedPatient && !selectedPatient.has_v2_context;
    if (notAllowed || notV2ForNurse) {
      setSelectedPatient(null);
    }
  }, [selectedMode, selectedPatient]);

  useEffect(() => {
    logger.info('🎭 PersonaSelection mounted with auth state:', { 
      hasUser: !!user, 
      isGuest, 
      isAuthenticated 
    });
    fetchPersonas();
  }, [user, isGuest, fetchPersonas, isAuthenticated]);

  const handleStartSimulation = async () => {
    if (!selectedPatient) {
      setError('Please select a patient first.');
      return;
    }

    logger.info('🚀 Starting simulation with:', {
      patient: selectedPatient.name,
      software: selectedSoftware?.name || 'none',
      superDoctor: selectedSuperDoctor?.name || 'none'
    });
    
    setStartingSimulation(true);
    setError('');
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const requestData = {
        mode: selectedMode,
        ...(selectedPatient.source === 'custom'
          ? { customPatientId: selectedPatient.id }
          : { personaId: selectedPatient.id }),
        ...(selectedMode !== 'nurse' && selectedSoftware && { softwarePersonaId: selectedSoftware.id }),
        ...(selectedMode !== 'nurse' && selectedSuperDoctor && { superDoctorId: selectedSuperDoctor.id }),
      };

      const response = await axios.post('/api/simulation/start', requestData, { headers });
      
      if (response.data.sessionId) {
        setShowConfirmDialog(false);
        navigate(`/simulation/${response.data.sessionId}`);
      } else {
        throw new Error('No session ID returned from server');
      }
    } catch (error) {
      logger.error('❌ Failed to start simulation:', error);
      setError('Failed to start simulation. Please try again.');
    } finally {
      setStartingSimulation(false);
    }
  };


  if (loading) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={48} />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Typography variant="h4" component="h1" fontWeight={600} sx={{ mb: 4 }}>
        Simulation Setup
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box component="section" sx={{ mb: 4 }}>
        <Typography variant="h5" sx={setupSectionTitleSx}>
          <ModeIcon sx={{ mr: 1, color: 'primary.main' }} />
          Mode
        </Typography>
        <Paper variant="outlined" sx={{ mt: 2, p: 2.5 }}>
          <SessionModeToggle
            value={selectedMode}
            onChange={setSelectedMode}
            align="start"
            modeOptions={sessionModeOptions}
          />
        </Paper>
      </Box>

      <Box component="section">
        <Typography variant="h5" sx={setupSectionTitleSx}>
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          Select Patient
        </Typography>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by name, patient number, chief complaint, age, or gender..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
        }}
        sx={{ mt: 2, mb: 2 }}
      />

      <Paper variant="outlined" sx={{ mb: 4, maxHeight: 600, overflow: 'auto' }}>
        <List>
          {selectablePatients.map((patient) => (
            <ListItem key={`${patient.source || 'library'}-${patient.id}`} disablePadding>
              <ListItemButton
                selected={selectedPatient?.id === patient.id}
                onClick={() => setSelectedPatient(patient)}
                sx={{
                  border: selectedPatient?.id === patient.id ? 2 : 1,
                  borderColor: selectedPatient?.id === patient.id ? 'primary.main' : 'transparent',
                  borderRadius: 1,
                  mb: 0.5,
                  mx: 1,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'primary.light'
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light'
                    }
                  }
                }}
              >
                <ListItemAvatar sx={{ minWidth: 80, mr: 1 }}>
                  {patient.source === 'custom' ? (
                    <CustomPatientProfileAvatar patient={patient} size={64} />
                  ) : (
                    <PatientProfileAvatar persona={patient} size={64} />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                      {patient.name}
                      {patient.source === 'custom' ? (
                        <Typography component="span" variant="caption" color="secondary.main" sx={{ ml: 1 }}>
                          (My Patient)
                        </Typography>
                      ) : null}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {patient.age} years • {patient.gender}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <Typography component="span" variant="subtitle2" color="primary.main" fontWeight={600}>
                          Chief Complaint:
                        </Typography>
                        {' '}{patient.chief_complaint}
                      </Typography>
                      {patient.social_background && (
                        <Typography variant="body2" color="text.secondary">
                          <Typography component="span" variant="subtitle2" color="secondary.main" fontWeight={600}>
                            Social Background:
                          </Typography>
                          {' '}{patient.social_background}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        {selectedMode === 'medical_diagnosis' && customPatients.length < customTotal && (
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={loadMoreCustomPatients}
              disabled={customLoadingMore}
            >
              {customLoadingMore ? 'Loading…' : 'Load more of my patients'}
            </Button>
          </Box>
        )}

        {selectablePatients.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No patients found matching your search criteria.
            </Typography>
          </Box>
        )}
      </Paper>
      </Box>

      {/* Start Simulation Button */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={() => setShowConfirmDialog(true)}
          disabled={!selectedPatient}
          sx={{ px: 4, py: 1.5 }}
        >
          Start Simulation
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog 
        open={showConfirmDialog} 
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" fontWeight={600}>
            Start Simulation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom color="text.secondary" sx={{ mb: 3 }}>
            You are about to start a medical interview simulation with:
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            Mode: {sessionModeLabel(selectedMode)}
          </Alert>
          
          {selectedPatient && (
            <Paper elevation={0} sx={{ p: 3, mb: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {selectedPatient.source === 'custom' ? (
                  <CustomPatientProfileAvatar patient={selectedPatient} size={40} sx={{ mr: 2 }} />
                ) : (
                  <PatientProfileAvatar persona={selectedPatient} size={40} sx={{ mr: 2 }} />
                )}
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Patient: {selectedPatient.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPatient.age} years old • {selectedPatient.gender}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                <strong>Chief Complaint:</strong> {selectedPatient.chief_complaint}
              </Typography>
              {selectedPatient.social_background && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Social Background:</strong> {selectedPatient.social_background}
                </Typography>
              )}
            </Paper>
          )}

          {selectedMode !== 'nurse' && selectedSuperDoctor && (
            <Paper elevation={0} sx={{ p: 3, mb: 2, bgcolor: PERSONA_COLORS.doctor.bg }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: PERSONA_COLORS.doctor.main, mr: 2 }}>
                  <SuperDoctorIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Attending Physician: {selectedSuperDoctor.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expert consultation during the simulation
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {selectedMode !== 'nurse' && selectedSoftware && (
            <Paper elevation={0} sx={{ p: 3, mb: 2, bgcolor: PERSONA_COLORS.software.bg }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: PERSONA_COLORS.software.main, mr: 2 }}>
                  <SoftwareIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Software Assistant: {selectedSoftware.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Medical Software Assistant
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              {selectedMode === 'nurse'
                ? 'In nurse mode you only talk with the patient about their medical history.'
                : selectedSoftware && selectedSuperDoctor
                  ? `You can communicate with the patient normally, control software with "Software", "System", "Computer", or "${selectedSoftware.name}", or consult the Attending Physician with "Doctor", "Professor", or their name.`
                  : 'This simulation will help you practice your clinical interview skills with the patient.'}
            </Typography>
          </Alert>

          {isGuest && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Guest Mode:</strong> This simulation will not be saved to your history.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setShowConfirmDialog(false)}
            disabled={startingSimulation}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStartSimulation}
            variant="contained"
            disabled={startingSimulation}
            startIcon={startingSimulation ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          >
            {startingSimulation ? 'Starting...' : 'Start Simulation'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default PersonaSelection;
